import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { Children } from "../../../../../../../../../components/miscellianous/children"
import { BSBuildingResultContext } from "../../../../../../../../../core/context/beem-shot/BSBuildingResult/BSBuildingResultContext"
import { BSMorphology } from "../../../../../../../../../core/dto/beem-shot/BSMorphology/BSMorphology"
import { BSMorphologyCreationDto } from "../../../../../../../../../core/dto/beem-shot/BSMorphology/BSMorphologyCreationDto"
import { BSMorphologyUpdateDto } from "../../../../../../../../../core/dto/beem-shot/BSMorphology/BSMorphologyUpdateDto"
import { useBSMorphology } from "../../../../../../../../../core/hooks/beem-shot/useBSMorphology"
import { BSBuildingContext } from "../../../../building/components/context/BSBuildingContext"

export const BSMorphologyContext = React.createContext<BSMorphologyStore>({} as BSMorphologyStore)

export function BSMorphologyContextProvider({ children }: Readonly<Children>): React.JSX.Element {
  const { createBSMorphology, putBSMorphology, getBSMorphology, createBSItemsForMorphology } = useBSMorphology()

  const { selectedBSBuilding, startEstimating } = useContext(BSBuildingContext)
  const { refreshBuildingResult } = useContext(BSBuildingResultContext)

  const [bsMorphology, setBSMorphology] = useState<BSMorphology | undefined>()
  const [isMorphologyLoaded, setIsMorphologyLoaded] = useState<boolean>(false)
  const [isEstimating, setIsEstimating] = useState<boolean>(false)

  const refreshBSMorphology: () => void = useCallback(() => {
    if (!selectedBSBuilding?.bim && selectedBSBuilding?.id) {
      getBSMorphology(selectedBSBuilding?.id)
        .then((buildingMorphology: BSMorphology) => {
          setBSMorphology(buildingMorphology)
          return buildingMorphology
        })
        .finally(() => setIsMorphologyLoaded(true))
    } else {
      setBSMorphology(undefined)
    }
  }, [getBSMorphology, selectedBSBuilding?.bim, selectedBSBuilding?.id])

  useEffect(() => {
    refreshBSMorphology()
  }, [refreshBSMorphology])

  const createNewBSMorphology: (bsMorphologyCreationDto: BSMorphologyCreationDto) => Promise<BSMorphology> = useCallback(
    (bsMorphologyCreationDto) =>
      createBSMorphology(bsMorphologyCreationDto).then((newBSMorphology) => {
        setBSMorphology(newBSMorphology)
        return newBSMorphology
      }),
    [createBSMorphology]
  )

  const updateBSMorphology: (bsMorphologyUpdateDto: BSMorphologyUpdateDto) => Promise<BSMorphology> = useCallback(
    (bsMorphologyUpdateDto) =>
      putBSMorphology(bsMorphologyUpdateDto).then((updatedBSMorphology) => {
        setBSMorphology(updatedBSMorphology)
        return updatedBSMorphology
      }),
    [putBSMorphology]
  )

  const startEstimatingMorphology: (buildingId: string) => Promise<void> = useCallback(
    (buildingId) => {
      setIsEstimating(true)
      return createBSItemsForMorphology(buildingId)
        .then(() => refreshBSMorphology())
        .then(() => startEstimating(buildingId))
        .then(() => refreshBuildingResult())
        .finally(() => setIsEstimating(false))
    },
    [createBSItemsForMorphology, refreshBSMorphology, refreshBuildingResult, startEstimating]
  )

  const bsMorphologyStore = useMemo(
    () => ({
      bsMorphology,
      isMorphologyLoaded,
      isEstimating,
      createNewBSMorphology,
      updateBSMorphology,
      startEstimatingMorphology,
      refreshBSMorphology,
    }),
    [
      bsMorphology,
      isMorphologyLoaded,
      isEstimating,
      createNewBSMorphology,
      updateBSMorphology,
      startEstimatingMorphology,
      refreshBSMorphology,
    ]
  )
  return <BSMorphologyContext.Provider value={bsMorphologyStore}>{children}</BSMorphologyContext.Provider>
}

export interface BSMorphologyStore {
  bsMorphology: BSMorphology | undefined
  isMorphologyLoaded: boolean
  isEstimating: boolean
  createNewBSMorphology(bsMorphologyCreationDto: BSMorphologyCreationDto): Promise<BSMorphology>
  updateBSMorphology(bsMorphologyUpdateDto: BSMorphologyUpdateDto): Promise<BSMorphology>
  startEstimatingMorphology(buildingId: string): Promise<void>
  refreshBSMorphology(): void
}
